import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AnimatedHeightContainerComponent,
  InfoBannerComponent,
  ModalComponent,
} from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformAuthModalPanel } from '../../enums/platform-auth-modal-panel.enum';
import { PlatformGoogleSigninComponent } from '../../modules/authentication/components/google-signin/platform-google-signin.component';
import { PlatformLoginFormComponent } from '../../modules/authentication/components/login-form/platform-login-form.component';
import { PlatformRegisterFormComponent } from '../../modules/authentication/components/register-form/platform-register-form.component';
import { PlatformResetPasswordFormComponent } from '../../modules/authentication/components/reset-password-form/platform-reset-password-form.component';
import { PlatformSetPasswordFormComponent } from '../../modules/authentication/components/set-password-form/platform-set-password-form.component';
import { PlatformVerifyEmailFormComponent } from '../../modules/authentication/components/verify-email-form/platform-verify-email-form.component';
import { PlatformApplyWithProfileService } from '../../services/platform-apply-with-profile.service';
import { PlatformAuthService } from '../../services/platform-auth.service';
import {
  PlatformAuthModalOptions,
  PlatformJobboardLoginModalService,
} from '../../services/platform-jobboard-login-modal.service';

@Component({
  selector: 'app-platform-jobboard-login-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalComponent,
    AnimatedHeightContainerComponent,
    TranslocoPipe,
    InfoBannerComponent,
    PlatformLoginFormComponent,
    PlatformRegisterFormComponent,
    PlatformResetPasswordFormComponent,
    PlatformSetPasswordFormComponent,
    PlatformVerifyEmailFormComponent,
    PlatformGoogleSigninComponent,
  ],
  template: `
    <ct-lib-modal
      #modal
      [small]="true"
      [medium]="false"
      [hideFooter]="true"
      [label]="titleKey() | transloco"
      (onClose)="onModalClosed()"
    >
      <app-animated-height-container [hideOverflow]="true">
      <div class="login-modal">
        @switch (panel()) {
          @case (panels.LOGIN) {
            <p>
              {{ 'jobboard.platform.auth.login.back-label' | transloco }}
              <a
                href="#"
                (click)="switchPanel($event, panels.REGISTER)"
                (keydown.enter)="switchPanel($event, panels.REGISTER)"
              >{{ 'jobboard.platform.auth.login.back-link' | transloco }}</a>
            </p>
            @if (registerSuccess()) {
              <ct-lib-info-banner type="success" class="mt-0 mb-0">
                <p class="text-start">
                  {{ 'jobboard.platform.auth.register.success-toast' | transloco }}
                </p>
              </ct-lib-info-banner>
            }
            <div class="d-flex flex-column gap-3 auth-actions">
              <app-platform-google-signin
                [label]="'jobboard.platform.auth.login.form.google-btn' | transloco"
                [loading]="loading()"
                [redirectUrl]="redirectUrl()"
                (onLoading)="loading.set($event)"
                (loggedIn)="onLoggedIn()"
              />
              <div class="separator">
                <span>{{ 'jobboard.platform.auth.login.separator-text' | transloco }}</span>
              </div>
              <app-platform-login-form
                [compact]="true"
                [loading]="loading()"
                [redirectUrl]="redirectUrl()"
                (onLoading)="loading.set($event)"
                (loggedIn)="onLoggedIn()"
                (forgotPassword)="showPanel(panels.RESET_PASSWORD)"
              />
            </div>
            <div class="more-options">
              <p>{{ 'jobboard.platform.auth.login.footer.label' | transloco }}</p>
              <p>
                <a
                  href="#"
                  (click)="switchPanel($event, panels.REGISTER)"
                  (keydown.enter)="switchPanel($event, panels.REGISTER)"
                >{{ 'jobboard.platform.auth.login.footer.option1' | transloco }}</a>
                {{ 'jobboard.platform.auth.login.footer.or' | transloco }}
                <a
                  href="#"
                  (click)="switchPanel($event, panels.VERIFY_EMAIL)"
                  (keydown.enter)="switchPanel($event, panels.VERIFY_EMAIL)"
                >{{ 'jobboard.platform.auth.login.footer.option2' | transloco }}</a>
              </p>
            </div>
          }
          @case (panels.REGISTER) {
            <p>
              {{ 'jobboard.platform.auth.register.back-label' | transloco }}
              <a
                href="#"
                (click)="switchPanel($event, panels.LOGIN)"
                (keydown.enter)="switchPanel($event, panels.LOGIN)"
              >{{ 'jobboard.platform.auth.register.back-link' | transloco }}</a>
            </p>
            <ct-lib-info-banner>
              <p>{{ 'jobboard.platform.auth.register.info' | transloco }}</p>
            </ct-lib-info-banner>
            <div class="d-flex flex-column gap-3 auth-actions">
              <app-platform-google-signin
                [label]="'jobboard.platform.auth.register.google-btn' | transloco"
                [loading]="loading()"
                [isRegistration]="true"
                [redirectUrl]="redirectUrl()"
                (onLoading)="loading.set($event)"
                (loggedIn)="onLoggedIn()"
              />
              <div class="separator">
                <span>{{ 'jobboard.platform.auth.login.separator-text' | transloco }}</span>
              </div>
              <app-platform-register-form
                [prefillEmail]="prefillEmail()"
                (onLoading)="loading.set($event)"
              />
            </div>
          }
          @case (panels.RESET_PASSWORD) {
            <p>
              {{ 'jobboard.platform.auth.reset-password.back-label' | transloco }}
              <a
                href="#"
                (click)="switchPanel($event, panels.LOGIN)"
                (keydown.enter)="switchPanel($event, panels.LOGIN)"
              >{{ 'jobboard.platform.auth.reset-password.back-link' | transloco }}</a>
            </p>
            <div class="auth-actions">
              <app-platform-reset-password-form />
            </div>
          }
          @case (panels.SET_PASSWORD) {
            <p>
              {{ 'jobboard.platform.auth.set-password.back-label' | transloco }}
              <a
                href="#"
                (click)="switchPanel($event, panels.LOGIN)"
                (keydown.enter)="switchPanel($event, panels.LOGIN)"
              >{{ 'jobboard.platform.auth.set-password.back-link' | transloco }}</a>
            </p>
            <div class="auth-actions">
              <app-platform-set-password-form
                [nonce]="nonce()"
                [isPasswordReset]="isPasswordReset()"
                (completed)="onSetPasswordCompleted($event)"
              />
            </div>
          }
          @case (panels.VERIFY_EMAIL) {
            @if (!nonce()) {
              <p>
                {{ 'jobboard.platform.auth.verify-email.back-label' | transloco }}
                <a
                  href="#"
                  (click)="switchPanel($event, panels.LOGIN)"
                  (keydown.enter)="switchPanel($event, panels.LOGIN)"
                >{{ 'jobboard.platform.auth.verify-email.back-link' | transloco }}</a>
              </p>
            } @else {
              <p>
                <a
                  href="#"
                  (click)="switchPanel($event, panels.LOGIN)"
                  (keydown.enter)="switchPanel($event, panels.LOGIN)"
                >{{ 'jobboard.platform.auth.verify-email.back-link' | transloco }}</a>
              </p>
            }
            <div class="auth-actions">
              <app-platform-verify-email-form [nonce]="nonce()" />
            </div>
          }
        }
      </div>
      </app-animated-height-container>
    </ct-lib-modal>
  `,
  styleUrl: './platform-jobboard-login-modal.component.scss',
})
export class PlatformJobboardLoginModalComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(PlatformAuthService);
  private readonly loginModalService = inject(PlatformJobboardLoginModalService);
  private readonly applyWithProfile = inject(PlatformApplyWithProfileService);
  private readonly modal = viewChild.required<ModalComponent>('modal');

  readonly panels = PlatformAuthModalPanel;
  readonly panel = signal(PlatformAuthModalPanel.LOGIN);
  readonly loading = signal(false);
  /** `null` = dashboard; URL = navigate/stay depending on current route. */
  readonly redirectUrl = signal<string | null>(null);
  readonly prefillEmail = signal<string | null>(null);
  readonly nonce = signal<string | null>(null);
  readonly registerSuccess = signal(false);
  readonly isPasswordReset = signal(false);

  readonly titleKey = computed(() => {
    switch (this.panel()) {
      case PlatformAuthModalPanel.REGISTER:
        return 'jobboard.platform.auth.register.title';
      case PlatformAuthModalPanel.RESET_PASSWORD:
        return 'jobboard.platform.auth.reset-password.title';
      case PlatformAuthModalPanel.SET_PASSWORD:
        return 'jobboard.platform.auth.set-password.title';
      case PlatformAuthModalPanel.VERIFY_EMAIL:
        return 'jobboard.platform.auth.verify-email.title';
      default:
        return 'jobboard.platform.auth.login.title';
    }
  });

  constructor() {
    this.loginModalService.register((options) => this.open(options));
  }

  /**
   * Opens the jobboard auth modal for the requested panel.
   * @param options - Panel and optional context
   */
  open(options: PlatformAuthModalOptions = {}): void {
    this.panel.set(options.panel ?? PlatformAuthModalPanel.LOGIN);
    if (options.redirectUrl !== undefined) {
      this.redirectUrl.set(options.redirectUrl);
    } else {
      const currentUrl = this.router.url;
      // Stay on the current page unless opened from a legacy /auth URL.
      this.redirectUrl.set(currentUrl.startsWith('/auth') ? null : currentUrl);
    }
    this.prefillEmail.set(options.email ?? null);
    this.nonce.set(options.nonce ?? null);
    this.registerSuccess.set(!!options.registerSuccess);
    this.isPasswordReset.set(!!options.isPasswordReset);
    this.loading.set(false);
    this.modal().open();
  }

  /**
   * Switches the visible auth panel without closing the modal.
   * @param panel - Target panel
   */
  showPanel(panel: PlatformAuthModalPanel): void {
    this.panel.set(panel);
    if (panel !== PlatformAuthModalPanel.VERIFY_EMAIL && panel !== PlatformAuthModalPanel.SET_PASSWORD) {
      this.nonce.set(null);
    }
    if (panel !== PlatformAuthModalPanel.LOGIN) {
      this.registerSuccess.set(false);
    }
  }

  /**
   * Handles in-modal navigation link clicks.
   * @param event - Click or keyboard event
   * @param panel - Target panel
   */
  switchPanel(event: Event, panel: PlatformAuthModalPanel): void {
    event.preventDefault();
    this.showPanel(panel);
  }

  /**
   * Closes the modal after a successful login and runs pending follow-up actions.
   */
  onLoggedIn(): void {
    this.modal().close();
    this.loginModalService.runAfterLogin();
    this.applyWithProfile.resumeAfterLogin();
  }

  /**
   * Opens the login panel after a password was set from an email link.
   * @param result - Whether registration success messaging should be shown
   */
  onSetPasswordCompleted(result: { registerSuccess: boolean }): void {
    this.registerSuccess.set(result.registerSuccess);
    this.showPanel(PlatformAuthModalPanel.LOGIN);
  }

  /**
   * Resets transient modal state when the dialog is dismissed.
   */
  onModalClosed(): void {
    this.loading.set(false);
    if (!this.auth.loggedIn()) {
      this.loginModalService.clearAfterLogin();
    }
  }
}
